import { useEffect, useState } from 'react';
import { setCallCenters } from 'src/actions/callCenterActions';
import { useDispatch, useSelector } from 'react-redux';

function useCallCenter() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.account);
  const { callCenters: allCallCenters } = useSelector((state) => state.callCenters);

  const activeCallCenters = allCallCenters?.filter((callCenter) => callCenter.active ?? true) ?? [];
  const activeCallCenterIds = activeCallCenters?.map(({ id }) => id);
  const activeCallCenterIdsStringified = activeCallCenterIds?.join(',') ?? '';
  const [selectedCallCenters, setSelectedCallCenters] = useState({});

  useEffect(() => {
    if (!allCallCenters) return;

    setSelectedCallCenters(
      allCallCenters.reduce((callCentersObj, curr) => {
        callCentersObj[curr.id] = { ...curr, active: curr.active ?? true };
        return callCentersObj;
      }, {})
    );
  }, [allCallCenters]);

  const areAllCallCentersDeselected = () => {
    for (const selected of Object.values(selectedCallCenters)) {
      if (selected.active) return false;
    }

    return true;
  };

  const toggleSelectedCallCenter = (callCenterId) => () => {
    setSelectedCallCenters((prevSelectedCallCenters) => ({
      ...prevSelectedCallCenters,
      [callCenterId]: {
        ...prevSelectedCallCenters[callCenterId],
        active: !prevSelectedCallCenters[callCenterId]?.active
      },
    }));
  };

  const areAllCallCentersSelected = () => {
    const selectedCallCenterValues = Object.values(selectedCallCenters);
    return selectedCallCenterValues.every((selected) => selected.active);
  };

  const toggleAllSelectedCallCenters = () => {
    const areAllSelected = areAllCallCentersSelected();
    setSelectedCallCenters((prevSelectedCallCenters) => Object.keys(prevSelectedCallCenters).reduce(
      (callCentersObj, curr) => ({
        ...callCentersObj,
        [curr]: {
          ...prevSelectedCallCenters[curr],
          active: !areAllSelected
        }
      }),
      {}
    ));
  };

  const getAdminCallCenter = () => allCallCenters?.find((callCenter) => callCenter.isAdmin);

  const isCallCenterSelected = (id) => selectedCallCenters[id]?.active === true;

  const saveCallCenters = () => {
    const newCallCenters = allCallCenters.map((callCenter) => ({ ...callCenter, active: selectedCallCenters[callCenter.id]?.active }));

    if (Object
      .values(selectedCallCenters)
      .filter((callCenter) => callCenter.active)
      .length === 0
    ) {
      throw new Error('Please select at least one call center.');
    }

    dispatch(setCallCenters(user, newCallCenters));
  };

  return {
    allCallCenters,
    activeCallCenterIds,
    activeCallCenters,
    selectedCallCenters,
    areAllCallCentersDeselected,
    toggleSelectedCallCenter,
    isCallCenterSelected,
    areAllCallCentersSelected,
    toggleAllSelectedCallCenters,
    saveCallCenters,
    getAdminCallCenter,
    activeCallCenterIdsStringified,
  };
}

export default useCallCenter;
